import React, { useMemo } from 'react';

// Session time definitions (UTC hours)
const SESSION_TIMES = {
  sydney: { start: 21, end: 6, label: 'Sydney', color: '#8b5cf6' },  // 21:00-06:00 UTC
  tokyo: { start: 0, end: 9, label: 'Tokyo', color: '#ec4899' },      // 00:00-09:00 UTC
  london: { start: 7, end: 16, label: 'London', color: '#3b82f6' },   // 07:00-16:00 UTC
  newyork: { start: 12, end: 21, label: 'New York', color: '#22c55e' } // 12:00-21:00 UTC
};

// Session Timeline Component
const SessionTimeline = ({ selectedSessions }) => {
  const hours = Array.from({ length: 24 }, (_, i) => i);

  return (
    <div className="session-timeline">
      <div className="timeline-header">
        <span className="timeline-label">Session Coverage (UTC)</span>
        <span className="timeline-hint">Overlapping sessions = high liquidity</span>
      </div>
      <div className="timeline-container">
        <div className="timeline-hours">
          {[0, 6, 12, 18].map(h => (
            <span key={h} className="hour-label">{h.toString().padStart(2, '0')}:00</span>
          ))}
          <span className="hour-label">24:00</span>
        </div>
        <div className="timeline-bars">
          {Object.entries(SESSION_TIMES).map(([key, session]) => {
            const isSelected = selectedSessions?.includes(key);
            if (!isSelected) return null;

            // Calculate position and width (handling wrap-around)
            let segments = [];
            if (session.start > session.end) {
              // Wraps around midnight
              segments.push({ start: session.start, end: 24 });
              segments.push({ start: 0, end: session.end });
            } else {
              segments.push({ start: session.start, end: session.end });
            }

            return segments.map((seg, i) => (
              <div
                key={`${key}-${i}`}
                className="session-bar"
                style={{
                  left: `${(seg.start / 24) * 100}%`,
                  width: `${((seg.end - seg.start) / 24) * 100}%`,
                  backgroundColor: session.color,
                }}
                title={`${session.label}: ${session.start}:00 - ${session.end}:00 UTC`}
              >
                {i === 0 && <span className="session-bar-label">{session.label}</span>}
              </div>
            ));
          })}
        </div>
        <div className="timeline-overlap-indicator">
          {/* Calculate overlap zones */}
          {hours.map(hour => {
            const activeSessions = Object.entries(SESSION_TIMES).filter(([key, session]) => {
              if (!selectedSessions?.includes(key)) return false;
              if (session.start > session.end) {
                return hour >= session.start || hour < session.end;
              }
              return hour >= session.start && hour < session.end;
            });
            const overlapCount = activeSessions.length;
            return (
              <div
                key={hour}
                className={`overlap-cell overlap-${Math.min(overlapCount, 3)}`}
                title={overlapCount > 1 ? `${overlapCount} sessions overlap at ${hour}:00 UTC` : ''}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Validation rules for each config schema
const VALIDATION_RULES = {
  VolatilityGate: (config) => {
    const errors = {};
    if (config.minVolatilityPercent !== undefined && config.maxVolatilityPercent !== undefined) {
      if (config.minVolatilityPercent >= config.maxVolatilityPercent) {
        errors.minVolatilityPercent = 'Min must be less than Max';
        errors.maxVolatilityPercent = 'Max must be greater than Min';
      }
    }
    return errors;
  },
  MaxOpenRiskGate: (config) => {
    const errors = {};
    if (config.currentOpenRiskPercent !== undefined && config.maxOpenRiskPercent !== undefined) {
      if (config.currentOpenRiskPercent > config.maxOpenRiskPercent) {
        errors.currentOpenRiskPercent = 'Current risk exceeds maximum limit';
      }
    }
    return errors;
  },
  EventProximityGate: (config) => {
    const errors = {};
    if (config.blockBeforeMin !== undefined && config.blockBeforeMin < 5) {
      errors.blockBeforeMin = 'Minimum 5 minutes recommended';
    }
    return errors;
  },
};

// Rule-specific config schemas
const RULE_CONFIG_SCHEMAS = {
  MaxRiskGate: [
    { key: 'maxRiskPercent', label: 'Max Risk %', type: 'number', min: 0.1, max: 10, step: 0.1, default: 0.5 }
  ],
  MinRRGate: [
    { key: 'minRR', label: 'Minimum R:R', type: 'number', min: 0.5, max: 10, step: 0.1, default: 1.5 }
  ],
  // MaxOpenRiskGate is hidden until broker integration is available
  // MaxOpenRiskGate: [
  //   { key: 'maxOpenRiskPercent', label: 'Max Total Open Risk %', type: 'number', min: 1, max: 20, step: 0.5, default: 3.0, hint: 'Maximum combined risk across all open positions' },
  //   { key: 'currentOpenRiskPercent', label: 'Current Open Risk %', type: 'number', min: 0, max: 20, step: 0.1, default: 0, hint: 'Risk from existing positions (0 if no open trades)' }
  // ],
  EventProximityGate: [
    { key: 'blockBeforeMin', label: 'Block Before (minutes)', type: 'number', min: 5, max: 120, step: 5, default: 30 },
    { key: 'blockAfterMin', label: 'Block After (minutes)', type: 'number', min: 1, max: 60, step: 1, default: 5 },
    {
      key: 'minImpactLevel', label: 'Minimum Impact Level', type: 'select',
      options: ['HIGH', 'MEDIUM', 'LOW'],
      default: 'HIGH',
      hint: 'Block events with this impact or higher (HIGH=only major events, LOW=all events)'
    }
  ],
  LiquidityWindowGate: [
    {
      key: 'allowedSessions', label: 'Allowed Sessions', type: 'sessions',
      options: ['london', 'newyork', 'tokyo', 'sydney'],
      default: ['london', 'newyork'],
      hint: 'Select trading sessions with acceptable liquidity'
    },
    { key: 'blockWeekends', label: 'Block Weekend Trading', type: 'boolean', default: true },
    {
      key: 'timezone', label: 'Display Timezone', type: 'select',
      options: ['UTC', 'America/New_York', 'Europe/London', 'Asia/Tokyo', 'Australia/Sydney'],
      default: 'UTC'
    }
  ],
  VolatilityGate: [
    {
      key: 'mode', label: 'Mode', type: 'select',
      options: ['range', 'spike'],
      default: 'range',
      hint: 'Range = min/max volatility check, Spike = detect sudden volatility spikes'
    },
    {
      key: 'minVolatilityPercent',
      label: 'Min Volatility %',
      type: 'number',
      min: 0,
      max: 5,
      step: 0.1,
      default: 0.1,
      hint: 'Minimum ATR/Price ratio. Blocks if market too quiet. (Range mode)',
      stacked: true
    },
    {
      key: 'maxVolatilityPercent',
      label: 'Max Volatility %',
      type: 'number',
      min: 1,
      max: 20,
      step: 0.5,
      default: 5.0,
      hint: 'Maximum ATR/Price ratio. Blocks if market too volatile. (Range mode)',
      stacked: true
    },
    {
      key: 'maxRatio',
      label: 'Max ATR Ratio',
      type: 'number',
      min: 1.1,
      max: 3.0,
      step: 0.1,
      default: 1.5,
      hint: 'Block if ATR(5)/ATR(14) exceeds this ratio. (Spike mode)',
      stacked: true
    },
    {
      key: 'timeframe', label: 'Timeframe', type: 'select',
      options: ['M15', 'M30', 'H1', 'H4', 'D'],
      default: 'H1'
    }
  ],
  TrendAlignmentGate: [
    {
      key: 'mode', label: 'Mode', type: 'select',
      options: ['single', 'multi'],
      default: 'single',
      hint: 'Single timeframe or multi-timeframe alignment check'
    },
    {
      key: 'timeframe', label: 'Timeframe', type: 'select',
      options: ['M5', 'M15', 'M30', 'H1', 'H4', 'D', 'W'],
      default: 'D',
      hint: 'Higher timeframe to check trend direction (Single mode)'
    },
    {
      key: 'period', label: 'MA Period', type: 'select',
      options: [20, 50, 200],
      default: 200,
      hint: 'Moving average period (20, 50, or 200)'
    },
    {
      key: 'multiTimeframes', label: 'Multi-TF Checks', type: 'select',
      options: ['M5+M15', 'M15+H1', 'M5+M15+H1', 'H1+H4', 'H1+H4+D'],
      default: 'M5+M15+H1',
      hint: 'Predefined timeframe combinations (Multi mode)'
    },
    { key: 'requireAll', label: 'Require All Aligned', type: 'boolean', default: true }
  ],
  ExtensionGate: [
    {
      key: 'maPeriod', label: 'MA Period', type: 'select',
      options: [20, 50, 200],
      default: 20,
      hint: 'Mean baseline for extension calculation'
    },
    {
      key: 'atrMultiple',
      label: 'ATR Multiple',
      type: 'number',
      min: 1,
      max: 5,
      step: 0.5,
      default: 3.0,
      hint: 'How many ATRs from mean is "over-extended"',
      stacked: true
    },
    {
      key: 'timeframe', label: 'Timeframe', type: 'select',
      options: ['M15', 'M30', 'H1', 'H4', 'D'],
      default: 'H1'
    }
  ],
  IntradayAtrTargetGate: [
    {
      key: 'tradingStyle', label: 'Trading Style', type: 'select',
      options: ['intraday', 'swing'],
      default: 'intraday',
      hint: 'Intraday uses stricter limits (1.5x ATR), swing allows larger targets (3x ATR)'
    },
    {
      key: 'maxTargetAtrMultiple',
      label: 'Max Target (ATR Multiple)',
      type: 'number',
      min: 0.5,
      max: 5,
      step: 0.25,
      default: 1.5,
      hint: 'Maximum target distance as multiple of daily ATR',
      stacked: true
    },
    {
      key: 'atrTimeframe', label: 'ATR Timeframe', type: 'select',
      options: ['H1', 'H4', 'D'],
      default: 'D',
      hint: 'Timeframe for ATR calculation (Daily recommended for intraday)'
    },
    { key: 'warnOnly', label: 'Warn Only (Don\'t Block)', type: 'boolean', default: false }
  ],
  SpreadGate: [
    {
      key: 'maxSpreadTargetPercent',
      label: 'Max Spread % of Target',
      type: 'number',
      min: 1,
      max: 30,
      step: 1,
      default: 10,
      hint: 'Maximum spread as percentage of your target. E.g., 10% means 1 pip spread on 10 pip target.',
      stacked: true
    },
    {
      key: 'maxSpreadPips',
      label: 'Max Spread (Pips)',
      type: 'number',
      min: 1,
      max: 20,
      step: 0.5,
      default: 5.0,
      hint: 'Absolute maximum spread allowed regardless of target',
      stacked: true
    },
    {
      key: 'warnThresholdPercent',
      label: 'Warn Threshold %',
      type: 'number',
      min: 1,
      max: 15,
      step: 1,
      default: 5,
      hint: 'Show warning (but pass) if spread exceeds this %'
    },
    { key: 'useTypicalSpread', label: 'Use Typical Spread Estimates', type: 'boolean', default: true },
    {
      key: 'spreadPips',
      label: 'Override Spread (Pips)',
      type: 'number',
      min: 0,
      max: 50,
      step: 0.1,
      default: null,
      hint: 'Leave empty to use typical spread, or enter current broker spread',
      stacked: true
    }
  ],
  TimeToCloseGate: [
    {
      key: 'tradingStyle', label: 'Trading Style', type: 'select',
      options: ['intraday', 'swing'],
      default: 'intraday',
      hint: 'Swing trades skip this check (they hold overnight)'
    },
    {
      key: 'minTimeToCloseMinutes',
      label: 'Min Time to Close (Minutes)',
      type: 'number',
      min: 15,
      max: 180,
      step: 15,
      default: 60,
      hint: 'Minimum minutes required before session close',
      stacked: true
    },
    {
      key: 'primarySession', label: 'Primary Session', type: 'select',
      options: ['newyork', 'london', 'tokyo', 'sydney'],
      default: 'newyork',
      hint: 'Which session close time to use'
    },
    {
      key: 'timezone', label: 'Display Timezone', type: 'select',
      options: ['UTC', 'America/New_York', 'Europe/London', 'Asia/Tokyo', 'Australia/Sydney'],
      default: 'UTC'
    }
  ],
  EntryProximityGate: [
    {
      key: 'maxDistanceAtrMultiple',
      label: 'Max Distance (ATR Multiple)',
      type: 'number',
      min: 0.5,
      max: 5,
      step: 0.25,
      default: 2.0,
      hint: 'Max entry distance from current price (in ATR). Too far = unlikely to fill.',
      stacked: true
    },
    {
      key: 'minDistanceAtrMultiple',
      label: 'Min Distance (ATR Multiple)',
      type: 'number',
      min: 0,
      max: 1,
      step: 0.05,
      default: 0.1,
      hint: 'Below this distance, a market order may be more practical.',
      stacked: true
    },
    {
      key: 'timeframe', label: 'ATR Timeframe', type: 'select',
      options: ['M15', 'M30', 'H1', 'H4', 'D'],
      default: 'H1',
      hint: 'Timeframe for ATR calculation'
    },
    {
      key: 'lookbackCandles',
      label: 'Lookback Candles',
      type: 'number',
      min: 0,
      max: 50,
      step: 1,
      default: 10,
      hint: 'Check if price visited entry level recently (0 to disable)'
    },
    { key: 'checkRecentVisit', label: 'Check Recent Visit', type: 'boolean', default: true }
  ],
  MomentumConfirmationGate: [
    {
      key: 'timeframe', label: 'Candle Timeframe', type: 'select',
      options: ['M15', 'M30', 'H1', 'H4', 'D'],
      default: 'H1',
      hint: 'Timeframe of candles to analyze for momentum'
    },
    {
      key: 'lookbackCandles',
      label: 'Lookback Candles',
      type: 'number',
      min: 2,
      max: 10,
      step: 1,
      default: 3,
      hint: 'Number of recent candles to check'
    },
    {
      key: 'minConfirmingCandles',
      label: 'Min Confirming Candles',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 2,
      hint: 'Minimum candles that must confirm trade direction'
    },
    { key: 'requireSignificantBody', label: 'Require Significant Body', type: 'boolean', default: true },
    {
      key: 'minBodyAtrPercent',
      label: 'Min Body Size (% ATR)',
      type: 'number',
      min: 0.05,
      max: 0.5,
      step: 0.05,
      default: 0.15,
      hint: 'Minimum candle body size as % of ATR to count as significant',
      stacked: true
    },
    { key: 'allowCounterTrend', label: 'Allow Counter-Trend', type: 'boolean', default: false }
  ],
  SessionHighLowGate: [
    {
      key: 'session', label: 'Session', type: 'select',
      options: ['current', 'previous', 'asian', 'london', 'newyork'],
      default: 'current',
      hint: 'Which session high/low to use as S/R levels'
    },
    {
      key: 'proximityMode', label: 'Proximity Mode', type: 'select',
      options: ['atr', 'pips'],
      default: 'atr',
      hint: 'ATR-based (dynamic) or fixed pips threshold'
    },
    {
      key: 'proximityAtrPercent',
      label: 'Proximity Threshold (% ATR)',
      type: 'number',
      min: 0.1,
      max: 1,
      step: 0.05,
      default: 0.25,
      hint: 'How close to session H/L is "too close" (as % of ATR)',
      stacked: true
    },
    {
      key: 'proximityPips',
      label: 'Proximity Threshold (Pips)',
      type: 'number',
      min: 5,
      max: 50,
      step: 1,
      default: 15,
      hint: 'Fixed pip distance (used when mode is "pips")',
      stacked: true
    },
    {
      key: 'timeframe', label: 'ATR Timeframe', type: 'select',
      options: ['M15', 'M30', 'H1', 'H4', 'D'],
      default: 'H1',
      hint: 'Timeframe for ATR calculation'
    },
    { key: 'blockLongNearHigh', label: 'Block Long Near High', type: 'boolean', default: true },
    { key: 'blockShortNearLow', label: 'Block Short Near Low', type: 'boolean', default: true },
    { key: 'warnOnly', label: 'Warn Only (Don\'t Block)', type: 'boolean', default: false }
  ],
  ConsecutiveLossGate: [
    {
      key: 'maxConsecutiveLosses',
      label: 'Max Consecutive Losses',
      type: 'number',
      min: 1,
      max: 10,
      step: 1,
      default: 3,
      hint: 'Block trading after this many losses in a row',
      stacked: true
    },
    {
      key: 'cooldownMinutes',
      label: 'Cooldown Period (Minutes)',
      type: 'number',
      min: 0,
      max: 1440,
      step: 5,
      default: 30,
      hint: 'Minutes to wait before trading again after hitting loss limit (0 = no cooldown)',
      stacked: true
    },
    {
      key: 'lookbackTrades',
      label: 'Lookback Trades',
      type: 'number',
      min: 5,
      max: 50,
      step: 5,
      default: 10,
      hint: 'How many recent trades to check'
    },
    {
      key: 'sourceId',
      label: 'Filter by Source',
      type: 'text',
      default: '',
      placeholder: 'e.g., tradingview_webhook, mt4_bot',
      hint: 'Only count losses from this source (empty = all sources)'
    },
    { key: 'warnOnly', label: 'Warn Only (Don\'t Block)', type: 'boolean', default: false }
  ],
};

const HardcodedRuleForm = ({ libraryRule, userRule, config, onConfigChange, onValidationChange }) => {
  const ruleClass = userRule?.ruleClass || libraryRule?.ruleClass;
  const schema = RULE_CONFIG_SCHEMAS[ruleClass] || [];

  // Compute validation errors
  const validationErrors = useMemo(() => {
    const validator = VALIDATION_RULES[ruleClass];
    if (validator) {
      return validator(config);
    }
    return {};
  }, [ruleClass, config]);

  // Report validation status to parent
  React.useEffect(() => {
    if (onValidationChange) {
      const hasErrors = Object.keys(validationErrors).length > 0;
      onValidationChange(!hasErrors);
    }
  }, [validationErrors, onValidationChange]);

  const handleChange = (key, value) => {
    onConfigChange({ ...config, [key]: value });
  };

  const handleSessionToggle = (key, session, currentSessions) => {
    const sessions = currentSessions || [];
    const newSessions = sessions.includes(session)
      ? sessions.filter(s => s !== session)
      : [...sessions, session];
    handleChange(key, newSessions);
  };

  // Validate individual field against min/max
  const getFieldError = (field, value) => {
    // Check schema-level validation first
    if (validationErrors[field.key]) {
      return validationErrors[field.key];
    }
    // Check min/max bounds
    if (field.type === 'number' && value !== undefined) {
      if (field.min !== undefined && value < field.min) {
        return `Minimum value is ${field.min}`;
      }
      if (field.max !== undefined && value > field.max) {
        return `Maximum value is ${field.max}`;
      }
    }
    return null;
  };

  if (schema.length === 0) {
    return (
      <div className="no-config-message">
        This rule has no configurable parameters.
      </div>
    );
  }

  const renderField = (field) => {
    const value = config[field.key] ?? field.default;

    switch (field.type) {
      case 'boolean':
        return (
          <div key={field.key} className="form-field form-field-inline">
            <label className="form-label">{field.label}</label>
            <button
              type="button"
              className={`toggle-btn ${value ? 'active' : ''}`}
              onClick={() => handleChange(field.key, !value)}
            >
              {value ? 'Yes' : 'No'}
            </button>
          </div>
        );

      case 'select':
        return (
          <div key={field.key} className="form-field">
            <label className="form-label">{field.label}</label>
            <select
              value={value}
              onChange={(e) => handleChange(field.key, e.target.value)}
              className="form-input form-select"
            >
              {field.options.map(opt => (
                <option key={opt} value={opt}>{opt}</option>
              ))}
            </select>
          </div>
        );

      case 'text':
        return (
          <div key={field.key} className="form-field">
            <label className="form-label">
              {field.label}
              {field.hint && <span className="form-label-hint">{field.hint}</span>}
            </label>
            <input
              type="text"
              value={value || ''}
              onChange={(e) => handleChange(field.key, e.target.value)}
              placeholder={field.placeholder || ''}
              className="form-input"
            />
          </div>
        );

      case 'sessions':
        return (
          <div key={field.key} className="form-field">
            <label className="form-label">
              {field.label}
              {field.hint && <span className="form-label-hint">{field.hint}</span>}
            </label>
            <div className="session-grid">
              {field.options.map(session => (
                <button
                  key={session}
                  type="button"
                  className={`session-btn ${(value || []).includes(session) ? 'active' : ''}`}
                  onClick={() => handleSessionToggle(field.key, session, value)}
                  style={{
                    borderColor: (value || []).includes(session)
                      ? SESSION_TIMES[session]?.color
                      : undefined
                  }}
                >
                  <span
                    className="session-color-dot"
                    style={{ backgroundColor: SESSION_TIMES[session]?.color }}
                  />
                  {session.charAt(0).toUpperCase() + session.slice(1)}
                </button>
              ))}
            </div>
            {/* Session Timeline Visualization */}
            {(value || []).length > 0 && (
              <SessionTimeline selectedSessions={value || []} />
            )}
          </div>
        );

      case 'number':
      default:
        const fieldError = getFieldError(field, value);
        // Use stacked layout for fields with hints to improve readability
        const isStacked = field.stacked || (field.hint && field.hint.length > 30);
        return (
          <div key={field.key} className={`form-field ${fieldError ? 'has-error' : ''} ${isStacked ? 'form-field-stacked' : ''}`}>
            <label className="form-label">
              {field.label}
              {field.hint && <span className="form-label-hint">{field.hint}</span>}
            </label>
            <input
              type="number"
              inputMode="decimal"
              pattern="[0-9]*"
              value={value}
              onChange={(e) => handleChange(field.key, parseFloat(e.target.value))}
              min={field.min}
              max={field.max}
              step={field.step}
              className={`form-input ${fieldError ? 'input-error' : ''}`}
            />
            {fieldError ? (
              <div className="form-error">{fieldError}</div>
            ) : field.min !== undefined && field.max !== undefined ? (
              <div className="form-hint">
                Range: {field.min} - {field.max}
              </div>
            ) : null}
          </div>
        );
    }
  };

  return (
    <div className="hardcoded-rule-form">
      {schema.map(renderField)}
    </div>
  );
};

export default HardcodedRuleForm;
